import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import axios from "axios";
import Swal from "sweetalert2";

const API = process.env.REACT_APP_API_BASE_URL;

const HomeworkNotCompleted = () => {
  const [teacher, setTeacher] = useState(null);

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");

  // Teacher selection
  const [selectedStudents, setSelectedStudents] = useState({});

  // =====================================================
  // SUBMITTED REPORTS
  // =====================================================

  const [sentReports, setSentReports] = useState([]);
  const [allSentReports, setAllSentReports] = useState([]);

  // Edit submitted report
  const [editingReport, setEditingReport] = useState(null);

  // Default date range = Today
  const getToday = () => {
    const date = new Date();

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const [
    submittedReportFromDate,
    setSubmittedReportFromDate
  ] = useState(getToday());

  const [
    submittedReportToDate,
    setSubmittedReportToDate
  ] = useState(getToday());

  const [loading, setLoading] = useState(true);

  const [
    studentsLoading,
    setStudentsLoading
  ] = useState(false);

  const [
    subjectsLoading,
    setSubjectsLoading
  ] = useState(false);

  const [sending, setSending] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // =====================================================
  // SESSION
  // =====================================================

  const sessionId =
    localStorage.getItem("currentSessionId") ||
    localStorage.getItem("sessionId");

  // =====================================================
  // TEACHER ID
  // =====================================================

  const teacherId = useMemo(() => {
    try {
      const data = JSON.parse(
        localStorage.getItem("teacher") || "null"
      );

      return (
        data?._id ||
        data?.id ||
        data?.teacherId ||
        null
      );
    } catch {
      return null;
    }
  }, []);

  // =====================================================
  // TEACHER NAME
  // =====================================================

  const teacherName = useMemo(() => {
    if (!teacher) return "";

    return `${teacher.firstName || ""} ${
      teacher.lastName || ""
    }`.trim();
  }, [teacher]);

  // =====================================================
  // LOAD TEACHER
  // =====================================================

  useEffect(() => {
    const loadTeacher = async () => {
      if (!teacherId || !sessionId) {
        setLoading(false);

        setError(
          "Teacher or session missing."
        );

        return;
      }

      try {
        setLoading(true);
        setError("");

        const res = await axios.get(
          `${API}/api/teachers/getTeacherById/${teacherId}`,
          {
            params: {
              sessionId
            }
          }
        );

        setTeacher(res.data);
      } catch (err) {
        console.error(
          "Teacher loading error:",
          err
        );

        setError(
          err.response?.data?.message ||
            "Unable to load teacher."
        );
      } finally {
        setLoading(false);
      }
    };

    loadTeacher();
  }, [teacherId, sessionId]);

  // =====================================================
  // LOAD CLASSES FROM TIMETABLE
  // =====================================================

  useEffect(() => {
    const loadClasses = async () => {
      if (!teacherName || !sessionId) return;

      try {
        const res = await axios.get(
          `${API}/api/timetable/getTeacherClasses/${encodeURIComponent(
            teacherName
          )}`,
          {
            params: {
              sessionId
            }
          }
        );

        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.classes || [];

        const unique = [];

        data.forEach((item) => {
          const exists = unique.some(
            (x) =>
              String(x.className) ===
                String(item.className) &&
              String(x.sectionName) ===
                String(item.sectionName)
          );

          if (!exists) {
            unique.push(item);
          }
        });

        setClasses(unique);

        if (unique.length) {
          setSelectedClass(
            unique[0].className || ""
          );

          setSelectedSection(
            unique[0].sectionName || ""
          );
        }
      } catch (err) {
        console.error(
          "Classes loading error:",
          err
        );

        setClasses([]);
        setSections([]);

        setSelectedClass("");
        setSelectedSection("");
      }
    };

    loadClasses();
  }, [teacherName, sessionId]);

  // =====================================================
  // SECTIONS
  // =====================================================

  useEffect(() => {
    const sec = classes
      .filter(
        (x) =>
          String(x.className) ===
          String(selectedClass)
      )
      .map(
        (x) =>
          x.sectionName
      )
      .filter(Boolean);

    const uniqueSections = [
      ...new Set(sec)
    ];

    setSections(uniqueSections);

    if (
      uniqueSections.length &&
      !uniqueSections.includes(
        selectedSection
      )
    ) {
      setSelectedSection(
        uniqueSections[0]
      );
    }
  }, [
    selectedClass,
    classes,
    selectedSection
  ]);

  // =====================================================
  // CLASS CHANGE
  // =====================================================

  const handleClassChange = (e) => {
    const value = e.target.value;

    setSelectedClass(value);

    const classData = classes.find(
      (x) =>
        String(x.className) ===
        String(value)
    );

    setSelectedSection(
      classData?.sectionName || ""
    );

    setSelectedSubject("");
    setSubjects([]);
    setStudents([]);
    setSelectedStudents({});
  };

  // =====================================================
  // SECTION CHANGE
  // =====================================================

  const handleSectionChange = (e) => {
    const value = e.target.value;

    setSelectedSection(value);

    setSelectedSubject("");
    setSubjects([]);
    setStudents([]);
    setSelectedStudents({});
  };

  // =====================================================
  // SUBJECTS
  // =====================================================

  useEffect(() => {
    const loadSubjects = async () => {
      if (
        !teacherName ||
        !selectedClass ||
        !selectedSection ||
        !sessionId
      ) {
        return;
      }

      setSubjectsLoading(true);

      try {
        const res = await axios.get(
          `${API}/api/timetable/getSubjectsByTeacherAndClass/${encodeURIComponent(
            teacherName
          )}/${encodeURIComponent(
            selectedClass
          )}`,
          {
            params: {
              sessionId,
              sectionName:
                selectedSection
            }
          }
        );

        const data =
          res.data?.subjects || [];

        setSubjects(data);

        if (data.length === 1) {
          setSelectedSubject(
            data[0]
          );
        } else {
          setSelectedSubject("");
        }
      } catch (err) {
        console.error(
          "Subjects loading error:",
          err
        );

        setSubjects([]);
        setSelectedSubject("");
      } finally {
        setSubjectsLoading(false);
      }
    };

    loadSubjects();
  }, [
    teacherName,
    selectedClass,
    selectedSection,
    sessionId
  ]);

  // =====================================================
  // STUDENTS
  // =====================================================

  useEffect(() => {
    const loadStudents = async () => {
      if (
        !selectedClass ||
        !selectedSection ||
        !sessionId
      ) {
        setStudents([]);
        setSelectedStudents({});

        return;
      }

      setStudentsLoading(true);

      try {
        const res = await axios.get(
          `${API}/api/students/getStudents`,
          {
            params: {
              sessionId,
              class:
                selectedClass,
              section:
                selectedSection,
              status: "active"
            }
          }
        );

        const data =
          Array.isArray(res.data)
            ? res.data
            : res.data?.students || [];

        const sorted = [...data].sort(
          (a, b) =>
            Number(
              a.rollNo || 9999
            ) -
            Number(
              b.rollNo || 9999
            )
        );

        setStudents(sorted);

        setSelectedStudents({});
      } catch (err) {
        console.error(
          "Students loading error:",
          err
        );

        setStudents([]);
        setSelectedStudents({});
      } finally {
        setStudentsLoading(false);
      }
    };

    loadStudents();
  }, [
    selectedClass,
    selectedSection,
    sessionId
  ]);

  // =====================================================
  // LOAD MY SUBMITTED REPORTS
  // =====================================================

  const loadSentReports = async () => {
    if (!teacherId || !sessionId) return;

    try {
      const res = await axios.get(
        `${API}/api/homeworkNotCompleted/sent`,
        {
          params: {
            teacherId,
            sessionId
          }
        }
      );

      const data =
        Array.isArray(res.data)
          ? res.data
          : res.data?.reports || [];

      setAllSentReports(data);

      // Show selected range
      const filtered = data.filter(
        (report) => {
          if (!report.date) {
            return false;
          }

          return (
            report.date >=
              submittedReportFromDate &&
            report.date <=
              submittedReportToDate
          );
        }
      );

      setSentReports(filtered);
    } catch (err) {
      console.error(
        "Submitted reports loading failed:",
        err
      );

      setAllSentReports([]);
      setSentReports([]);

      Swal.fire({
        icon: "error",
        title: "Unable to Load Reports",
        text:
          err.response?.data
            ?.message ||
          "Submitted reports could not be loaded.",
        confirmButtonColor:
          "#2563eb"
      });
    }
  };

  // =====================================================
  // FILTER SUBMITTED REPORTS
  // =====================================================

  const handleSubmittedReportFilter = () => {
  if (
    !submittedReportFromDate ||
    !submittedReportToDate
  ) {
    return;
  }

  if (
    submittedReportFromDate >
    submittedReportToDate
  ) {
    return;
  }

  const filtered =
    allSentReports.filter((report) => {
      if (!report.date) {
        return false;
      }

      return (
        report.date >=
          submittedReportFromDate &&
        report.date <=
          submittedReportToDate
      );
    });

  setSentReports(filtered);
};

  // =====================================================
  // SHOW TODAY
  // =====================================================

  const handleSubmittedReportToday = () => {
  const today = getToday();

  setSubmittedReportFromDate(today);
  setSubmittedReportToDate(today);

  const todayReports =
    allSentReports.filter(
      (report) =>
        report.date === today
    );

  setSentReports(todayReports);
};

  useEffect(() => {
    loadSentReports();
  }, [
    teacherId,
    sessionId
  ]);

  // =====================================================
  // TOGGLE HOMEWORK / NOTEBOOK
  // =====================================================

  const toggleStudentType = (
    studentId,
    type
  ) => {
    setSelectedStudents(
      (prev) => {
        const current =
          prev[studentId] || {
            notCompletedHomework:
              false,

            notCompletedNotebook:
              false
          };

        const updated = {
          ...current,
          [type]:
            !current[type]
        };

        if (
          !updated.notCompletedHomework &&
          !updated.notCompletedNotebook
        ) {
          const next = {
            ...prev
          };

          delete next[
            studentId
          ];

          return next;
        }

        return {
          ...prev,

          [studentId]:
            updated
        };
      }
    );
  };

  // =====================================================
  // TICK / UNCHECK ALL
  // =====================================================

  const toggleAllStudents = (
    type
  ) => {
    const allChecked =
      students.length > 0 &&
      students.every(
        (student) =>
          selectedStudents[
            student._id
          ]?.[type]
      );

    setSelectedStudents(
      (prev) => {
        const next = {
          ...prev
        };

        students.forEach(
          (student) => {
            const current =
              next[
                student._id
              ] || {
                notCompletedHomework:
                  false,

                notCompletedNotebook:
                  false
              };

            next[
              student._id
            ] = {
              ...current,

              [type]:
                !allChecked
            };
          }
        );

        return next;
      }
    );
  };

  // =====================================================
  // MARKED COUNT
  // =====================================================

  const markedCount = useMemo(() => {
    return students.filter(
      (student) => {
        const status =
          selectedStudents[
            student._id
          ];

        return (
          status?.notCompletedHomework ||
          status?.notCompletedNotebook
        );
      }
    ).length;
  }, [
    students,
    selectedStudents
  ]);

  // =====================================================
  // EDIT SUBMITTED REPORT
  // =====================================================

  const handleEditReport = (report) => {
    if (!report?._id) return;

    setMessage("");
    setError("");

    setEditingReport(report);
    setSelectedClass(report.className || "");
    setSelectedSection(report.sectionName || "");
    setSelectedSubject(report.subject || "");
    setSelectedStudents({});
  };

  const cancelEditReport = () => {
    setEditingReport(null);
    setSelectedStudents({});
    setMessage("");
    setError("");
  };

  // When edit report is selected and students are loaded,
  // restore the old selections so teacher can add/remove students.
  useEffect(() => {
    if (!editingReport || !students.length) return;

    const restored = {};

    (editingReport.students || []).forEach((savedStudent) => {
      if (!savedStudent?.studentId) return;

      const studentExists = students.some(
        (student) =>
          String(student._id) === String(savedStudent.studentId)
      );

      if (!studentExists) return;

      if (
        savedStudent.notCompletedHomework ||
        savedStudent.notCompletedNotebook
      ) {
        restored[String(savedStudent.studentId)] = {
          notCompletedHomework:
            !!savedStudent.notCompletedHomework,
          notCompletedNotebook:
            !!savedStudent.notCompletedNotebook
        };
      }
    });

    setSelectedStudents(restored);
  }, [editingReport, students]);

  // =====================================================
  // SEND / UPDATE REPORT
  // =====================================================

  const handleSend = async () => {
    setMessage("");
    setError("");

    if (
      !selectedClass ||
      !selectedSection ||
      !selectedSubject
    ) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Selection",
        text: "Please select Class, Section and Subject.",
        confirmButtonColor: "#2563eb"
      });

      return;
    }

    const reportStudents =
      Object.entries(selectedStudents)
        .filter(
          ([, status]) =>
            status.notCompletedHomework ||
            status.notCompletedNotebook
        )
        .map(
          ([studentId, status]) => ({
            studentId,

            notCompletedHomework:
              !!status.notCompletedHomework,

            notCompletedNotebook:
              !!status.notCompletedNotebook
          })
        );

    if (!reportStudents.length && !editingReport) {
      Swal.fire({
        icon: "warning",
        title: "No Student Selected",
        text: "Please tick Homework or Notebook for at least one student.",
        confirmButtonColor: "#2563eb"
      });

      return;
    }

    // Normal submit: do not allow duplicate report.
    // Editing an existing report is allowed.
    if (!editingReport) {
      const duplicateReport = allSentReports.find(
        (report) =>
          String(report.className) === String(selectedClass) &&
          String(report.sectionName) === String(selectedSection) &&
          String(report.subject) === String(selectedSubject) &&
          report.date === getToday()
      );

      if (duplicateReport) {
        Swal.fire({
          icon: "warning",
          title: "Report Already Submitted",
          text:
            "This class data has already been submitted. You cannot submit it again. Please use Edit to add or change students.",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb"
        });

        return;
      }
    }

    setSending(true);

    try {
      const url = editingReport
        ? `${API}/api/homeworkNotCompleted/update/${editingReport._id}`
        : `${API}/api/homeworkNotCompleted/submit`;

      const res = await axios({
  method: editingReport ? "put" : "post",
  url,
  data: {
    teacherId,
    sessionId,
    className: selectedClass,
    sectionName: selectedSection,
    subject: selectedSubject,
    students: reportStudents
  }
});

      setSelectedStudents({});
      setEditingReport(null);

      setMessage(
        res.data?.message ||
          (editingReport
            ? "Report updated successfully."
            : "Report sent successfully.")
      );

      await loadSentReports();

      Swal.fire({
        icon: "success",
        title: editingReport
          ? "Report Updated"
          : "Report Submitted",
        text: editingReport
          ? "Homework / Notebook report has been updated successfully."
          : "Homework / Notebook report has been sent to the Class Incharge.",
        confirmButtonColor: "#2563eb",
        timer: 1800,
        showConfirmButton: false
      });
    } catch (err) {
      console.error(
        editingReport
          ? "Report update error:"
          : "Report sending error:",
        err
      );

      const errorMessage =
        err.response?.data?.message ||
        "Report submission failed.";

      setError(errorMessage);

      Swal.fire({
        icon: "error",
        title: editingReport
          ? "Report Update Failed"
          : "Report Submission Failed",
        text: errorMessage,
        confirmButtonColor: "#dc2626"
      });
    } finally {
      setSending(false);
    }
  };
    
  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="p-10 text-center">
        Loading...
      </div>
    );
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="bg-white border-b">
        <div className="px-4 sm:px-6 py-4 flex items-center gap-3">

          <i className="fas fa-clipboard-check text-yellow-500 text-xl"></i>

          <h1 className="text-xl font-bold">
            Homework Not Completed
          </h1>

        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto p-3 sm:p-5 space-y-5">

        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && (
          <div className="bg-green-100 text-green-700 px-4 py-3 rounded-lg">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-100 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* =================================================
            SUBMIT TODAY'S REPORT
        ================================================= */}

        <div className="bg-white border rounded-xl p-3 sm:p-5">

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold">
              {editingReport
                ? "Edit Submitted Report"
                : "Submit Today's Report"}
            </h2>

            {editingReport && (
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg">
                <i className="fas fa-edit"></i>
                Editing: {editingReport.subject} • {editingReport.date}
              </span>
            )}
          </div>

          {/* =================================================
              FILTERS
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* CLASS */}

            <div>

              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Class
              </label>

              <select
                value={
                  selectedClass
                }
                onChange={
                  handleClassChange
                }
                className="w-full border rounded-lg px-3 py-2.5 bg-white"
              >

                <option value="">
                  Select Class
                </option>

                {[
                  ...new Set(
                    classes.map(
                      (x) =>
                        x.className
                    )
                  )
                ].map((x) => (
                  <option
                    key={x}
                    value={x}
                  >
                    {x}
                  </option>
                ))}

              </select>

            </div>

            {/* SECTION */}

            <div>

              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Section
              </label>

              <select
                value={
                  selectedSection
                }
                onChange={
                  handleSectionChange
                }
                className="w-full border rounded-lg px-3 py-2.5 bg-white"
              >

                <option value="">
                  Select Section
                </option>

                {sections.map(
                  (x) => (
                    <option
                      key={x}
                      value={x}
                    >
                      {x}
                    </option>
                  )
                )}

              </select>

            </div>

            {/* SUBJECT */}

            <div>

              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Subject
              </label>

              <select
                value={
                  selectedSubject
                }
                onChange={(e) =>
                  setSelectedSubject(
                    e.target.value
                  )
                }
                className="w-full border rounded-lg px-3 py-2.5 bg-white"
              >

                <option value="">
                  {subjectsLoading
                    ? "Loading..."
                    : "Select Subject"}
                </option>

                {subjects.map(
                  (x) => (
                    <option
                      key={x}
                      value={x}
                    >
                      {x}
                    </option>
                  )
                )}

              </select>

            </div>

          </div>

          {/* =================================================
              STUDENTS
          ================================================= */}

          <div className="mt-5 border rounded-xl overflow-hidden">

            <div className="bg-gray-50 px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">

              <div>

                <b className="text-base sm:text-lg">
                  Students
                </b>

                <div className="text-xs sm:text-sm text-gray-500">

                  {markedCount} student(s)
                  marked out of{" "}
                  {students.length}

                </div>

              </div>

              <div className="text-xs sm:text-sm text-gray-500">
                Tick the relevant option for each student
              </div>

            </div>

            {/* =================================================
                TABLE
            ================================================= */}

            {studentsLoading ? (

              <div className="p-8 text-center text-gray-500">
                Loading students...
              </div>

            ) : students.length === 0 ? (

              <div className="p-8 text-center text-gray-500">
                No students found.
              </div>

            ) : (

              <div className="w-full overflow-hidden">

                <table
                  className="w-full text-xs sm:text-sm"
                  style={{
                    tableLayout:
                      "fixed",
                    width: "100%",
                    maxWidth:
                      "100%"
                  }}
                >

                  <colgroup>

                    <col
                      style={{
                        width: "5%"
                      }}
                    />

                    <col
                      style={{
                        width: "9%"
                      }}
                    />

                    <col
                      style={{
                        width: "21%"
                      }}
                    />

                    <col
                      style={{
                        width: "20%"
                      }}
                    />

                    <col
                      style={{
                        width: "22.5%"
                      }}
                    />

                    <col
                      style={{
                        width: "22.5%"
                      }}
                    />

                  </colgroup>

                  <thead className="bg-gray-100">

                    <tr>

                      <th className="px-1 sm:px-2 py-3 text-center font-semibold">
                        #
                      </th>

                      <th className="px-1 sm:px-2 py-3 text-center font-semibold">
                        Roll No
                      </th>

                      <th className="px-2 sm:px-3 py-3 text-left font-semibold">
                        Student Name
                      </th>

                      <th className="px-2 sm:px-3 py-3 text-left font-semibold">
                        Father Name
                      </th>

                      {/* HOMEWORK */}

                      <th className="px-1 sm:px-2 py-2 text-center font-semibold">

                        <div className="leading-tight">
                          Not Completed
                          <br />
                          Homework
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            toggleAllStudents(
                              "notCompletedHomework"
                            )
                          }
                          className="mt-1 text-xs font-semibold text-blue-600 hover:underline"
                        >

                          {students.length >
                            0 &&
                          students.every(
                            (s) =>
                              selectedStudents[
                                s._id
                              ]
                                ?.notCompletedHomework
                          )
                            ? "Uncheck All"
                            : "Tick All"}

                        </button>

                      </th>

                      {/* NOTEBOOK */}

                      <th className="px-1 sm:px-2 py-2 text-center font-semibold">

                        <div className="leading-tight">
                          Not Completed
                          <br />
                          Notebook
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            toggleAllStudents(
                              "notCompletedNotebook"
                            )
                          }
                          className="mt-1 text-xs font-semibold text-blue-600 hover:underline"
                        >

                          {students.length >
                            0 &&
                          students.every(
                            (s) =>
                              selectedStudents[
                                s._id
                              ]
                                ?.notCompletedNotebook
                          )
                            ? "Uncheck All"
                            : "Tick All"}

                        </button>

                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {students.map(
                      (
                        student,
                        index
                      ) => {

                        const status =
                          selectedStudents[
                            student._id
                          ] || {
                            notCompletedHomework:
                              false,

                            notCompletedNotebook:
                              false
                          };

                        const marked =
                          status.notCompletedHomework ||
                          status.notCompletedNotebook;

                        return (
                          <tr
                            key={
                              student._id
                            }
                            className={`border-t ${
                              marked
                                ? "bg-red-50"
                                : ""
                            }`}
                          >

                            {/* # */}

                            <td className="px-1 sm:px-2 py-3 text-center text-gray-500">
                              {index + 1}
                            </td>

                            {/* ROLL */}

                            <td className="px-1 sm:px-2 py-3 text-center font-medium">
                              {student.rollNo ||
                                "-"}
                            </td>

                            {/* STUDENT */}

                            <td className="px-2 sm:px-3 py-3 font-semibold text-gray-800 break-words">

                              {student.firstName}{" "}

                              {student.lastName ||
                                ""}

                            </td>

                            {/* FATHER */}

                            <td className="px-2 sm:px-3 py-3 text-gray-600 break-words">

                              {student.fatherName ||
                                "-"}

                            </td>

                            {/* HOMEWORK */}

                            <td className="px-1 sm:px-2 py-3 text-center">

                              <div className="flex justify-center items-center">

                                <input
                                  type="checkbox"

                                  checked={
                                    !!status.notCompletedHomework
                                  }

                                  onChange={() =>
                                    toggleStudentType(
                                      student._id,
                                      "notCompletedHomework"
                                    )
                                  }

                                  className="w-5 h-5 sm:w-5 sm:h-5 accent-red-600 cursor-pointer"
                                />

                              </div>

                            </td>

                            {/* NOTEBOOK */}

                            <td className="px-1 sm:px-2 py-3 text-center">

                              <div className="flex justify-center items-center">

                                <input
                                  type="checkbox"

                                  checked={
                                    !!status.notCompletedNotebook
                                  }

                                  onChange={() =>
                                    toggleStudentType(
                                      student._id,
                                      "notCompletedNotebook"
                                    )
                                  }

                                  className="w-5 h-5 sm:w-5 sm:h-5 accent-orange-600 cursor-pointer"
                                />

                              </div>

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            )}

            {/* =================================================
                SEND BUTTON
            ================================================= */}

            <div className="border-t bg-white px-3 sm:px-4 py-4 flex justify-end">

              <button
                type="button"
                onClick={
                  handleSend
                }
                disabled={
                  sending ||
                  students.length ===
                    0 ||
                  !selectedSubject
                }
                className={`w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition ${
                  sending ||
                  students.length ===
                    0 ||
                  !selectedSubject
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >

                {sending
                  ? editingReport
                    ? "Updating..."
                    : "Sending..."
                  : editingReport
                    ? "Update Report"
                    : "Send to Class Incharge"}

              </button>

              {editingReport && (
                <button
                  type="button"
                  onClick={cancelEditReport}
                  disabled={sending}
                  className="w-full sm:w-auto border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-lg font-semibold transition"
                >
                  Cancel Edit
                </button>
              )}

            </div>

          </div>

        </div>

        {/* =================================================
            MY SUBMITTED REPORTS
        ================================================= */}

        <div className="bg-white border rounded-xl p-3 sm:p-5">

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">

            <div>

              <h2 className="text-lg font-semibold">
                My Submitted Reports
              </h2>

              <p className="text-sm text-gray-500">
                Reports submitted by you to the Class Incharge.
              </p>

            </div>

            {/* =================================================
                DATE RANGE FILTER
            ================================================= */}

            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">

              {/* FROM DATE */}

              <div>

                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  From Date
                </label>

                <input
                  type="date"
                  value={
                    submittedReportFromDate
                  }
                  max={
                    submittedReportToDate ||
                    undefined
                  }
                  onChange={(e) =>
                    setSubmittedReportFromDate(
                      e.target.value
                    )
                  }
                  className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />

              </div>

              {/* TO DATE */}

              <div>

                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  To Date
                </label>

                <input
                  type="date"
                  value={
                    submittedReportToDate
                  }
                  min={
                    submittedReportFromDate ||
                    undefined
                  }
                  onChange={(e) =>
                    setSubmittedReportToDate(
                      e.target.value
                    )
                  }
                  className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />

              </div>

              {/* FILTER */}

              <button
                type="button"
                onClick={
                  handleSubmittedReportFilter
                }
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition"
              >

                <i className="fas fa-filter mr-2"></i>

                Filter

              </button>

              {/* TODAY */}

              <button
                type="button"
                onClick={
                  handleSubmittedReportToday
                }
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-semibold text-sm transition"
              >

                <i className="fas fa-calendar-day mr-2"></i>

                Today

              </button>

            </div>

          </div>

          {/* =================================================
              REPORT TABLE
          ================================================= */}

          {sentReports.length === 0 ? (

            <div className="text-center py-8 text-gray-500">
              No submitted reports found.
            </div>

          ) : (

            <div className="w-full overflow-hidden">

              <table
                className="w-full text-xs sm:text-sm"
                style={{
                  tableLayout:
                    "fixed",
                  width: "100%"
                }}
              >

                <colgroup>

                  <col
                    style={{
                      width: "14%"
                    }}
                  />

                  <col
                    style={{
                      width: "16%"
                    }}
                  />

                  <col
                    style={{
                      width: "16%"
                    }}
                  />

                  <col
                    style={{
                      width: "12%"
                    }}
                  />

                  <col
                    style={{
                      width: "18%"
                    }}
                  />

                  <col
                    style={{
                      width: "8%"
                    }}
                  />

                </colgroup>

                <thead className="bg-gray-100">

                  <tr>

                    <th className="px-2 sm:px-4 py-3 text-left">
                      Date
                    </th>

                    <th className="px-2 sm:px-4 py-3 text-left">
                      Class
                    </th>

                    <th className="px-2 sm:px-4 py-3 text-left">
                      Subject
                    </th>

                    <th className="px-2 sm:px-4 py-3 text-left">
                      Students
                    </th>

                    <th className="px-2 sm:px-4 py-3 text-left">
                      Class Incharge
                    </th>

                    <th className="px-2 sm:px-4 py-3 text-center">
                      Edit
                    </th>

                  </tr>

                </thead>

                <tbody>

                  {sentReports.map(
                    (report) => (

                      <tr
                        key={
                          report._id
                        }
                        className="border-t hover:bg-gray-50"
                      >

                        <td className="px-2 sm:px-4 py-3 text-left break-words">
                          {report.date ||
                            "-"}
                        </td>

                        <td className="px-2 sm:px-4 py-3 text-left break-words">

                          {report.className ||
                            "-"}

                          {" - "}

                          {report.sectionName ||
                            "-"}

                        </td>

                        <td className="px-2 sm:px-4 py-3 text-left break-words">

                          {report.subject ||
                            "-"}

                        </td>

                        <td className="px-2 sm:px-4 py-3 text-left">

                          {Array.isArray(
                            report.students
                          )
                            ? report.students.length
                            : 0}

                        </td>

                        <td className="px-2 sm:px-4 py-3 text-left break-words">

                          {report.classInchargeName ||
                            "-"}

                        </td>

                        <td className="px-2 sm:px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() =>
                              handleEditReport(report)
                            }
                            className="inline-flex items-center justify-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg font-semibold text-xs"
                            title="Edit this submitted report"
                          >
                            <i className="fas fa-edit"></i>
                            Edit
                          </button>
                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            </div>

          )}

        </div>

      </div>

      {/* =================================================
          RESPONSIVE FIX
      ================================================= */}

      <style>
        {`
          * {
            box-sizing: border-box;
          }

          body {
            overflow-x: hidden;
          }

          table {
            border-collapse: collapse;
          }

          @media (max-width: 640px) {
            table th,
            table td {
              font-size: 11px;
            }

            table th {
              line-height: 1.2;
            }
          }
        `}
      </style>

    </div>
  );

};

export default HomeworkNotCompleted;